<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { useRoute } from 'vue-router';
import FormInput from '@/elements/FormInput.vue';
import Checkbox from '@/elements/Checkbox.vue';
import api from '@/api';
import biologicsCover from '@/assets/images/handbooks/biologics.png';
import cellTherapyCover from '@/assets/images/handbooks/cell-therapy.png';
import discoveryCover from '@/assets/images/handbooks/discovery.png';
import geneTherapyCover from '@/assets/images/handbooks/gene-therapy.png';
import mrnaLnpCover from '@/assets/images/handbooks/mrna-lnp.png';
import {
  DocumentLibraryDocument,
  DocumentLibraryFilterOption,
  DocumentLibraryProps,
} from '@/types/components/DocumentLibrary';
import utils from '@/utils';

const props = defineProps<DocumentLibraryProps>();
const route = useRoute();

const normalizeSearchKeyword = (value: unknown) => {
  const keyword = Array.isArray(value) ? value[0] : value;
  return typeof keyword === 'string' ? keyword.trim() : '';
};

const rootClass = computed(() => utils.overall.baseComClass(props.fields, 'c-document-library'));
const rootStyle = computed(() => utils.overall?.baseComStyle?.(fields.value) || {});

const searchKeyword = ref(normalizeSearchKeyword(route.query.kw));
const selectedProductLines = ref<string[]>([]);
const selectedResourceTypes = ref<string[]>(['all-resources']);
const currentPage = ref(1);
const totalCount = ref(0);
const isLoading = ref(false);
const documents = ref<DocumentLibraryDocument[]>([]);
const mobileFiltersOpen = ref(false);
let filterReloadTimer: ReturnType<typeof setTimeout> | null = null;
let latestRequestId = 0;

const fields = computed(() => props.fields);

const productLineCovers: Record<string, string> = {
  biologics: biologicsCover,
  'cell-therapy': cellTherapyCover,
  discovery: discoveryCover,
  'gene-therapy': geneTherapyCover,
  'mrna-lnp': mrnaLnpCover,
};

const getFieldValue = (field: any, defaultValue = '') => field?.value ?? defaultValue;

const getRootFieldValue = (fieldName: string, defaultValue = '') => {
  const field = fields.value?.[
    fieldName as keyof NonNullable<DocumentLibraryProps['fields']>
  ] as any;
  return getFieldValue(field, defaultValue);
};

const pageSize = computed(() => {
  const value = (fields.value as any)?.pageSize?.value;
  const numericValue = Number(value);
  return Number.isFinite(numericValue) && numericValue > 0 ? numericValue : 10;
});

const totalPages = computed(() => Math.max(1, Math.ceil(totalCount.value / pageSize.value)));

const normalizeKey = (value?: string) =>
  String(value || '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '-');

const stripHtml = (value?: string) => String(value || '').replace(/<[^>]*>/g, '');

const getDictionaryText = (field: any) =>
  field?.fields?.text?.value ||
  field?.value?.text?.value ||
  field?.text?.value ||
  field?.displayName ||
  field?.name ||
  (typeof field?.value === 'string' ? field.value : '') ||
  '';

const getDictionaryValue = (field: any) =>
  field?.fields?.phrase?.value ||
  field?.value?.phrase?.value ||
  field?.phrase?.value ||
  field?.name ||
  (typeof field?.value === 'string' ? field.value : '') ||
  '';

const getFirstSelectedItem = (field: any) => {
  const value = field?.value;
  return Array.isArray(value) ? value[0] : field;
};

const normalizeOption = (item: any): DocumentLibraryFilterOption => ({
  id: String(item?.id ?? item?.fields?.phrase?.value ?? item?.name ?? ''),
  label: String(item?.fields?.text?.value ?? item?.displayName ?? item?.name ?? ''),
  value: normalizeKey(item?.fields?.phrase?.value ?? item?.name ?? ''),
});

const formatFileSize = (size: any) => {
  const numericSize = Number(size);
  if (!Number.isFinite(numericSize) || numericSize <= 0) return '';
  if (numericSize < 1024) return `${numericSize} B`;
  if (numericSize < 1024 * 1024) return `${(numericSize / 1024).toFixed(0)} KB`;
  return `${(numericSize / 1024 / 1024).toFixed(1)} MB`;
};

const getFileExtension = (file: any, fallback = 'PDF') => {
  const src = file?.value?.src || file?.src || '';
  const extension = src.split('?')[0].split('.').pop();
  return extension ? extension.toUpperCase() : fallback;
};

const normalizeDocument = (item: any): DocumentLibraryDocument => {
  const itemFields = item?.fields || {};
  const title = stripHtml(getFieldValue(itemFields.title) || item?.displayName || item?.name || '');
  const file = itemFields.file;
  const productLineValue = normalizeKey(
    getDictionaryValue(getFirstSelectedItem(itemFields.productLine))
  );
  const resourceTypeValue = normalizeKey(getDictionaryValue(itemFields.resourceType));
  const tagText = getFieldValue(itemFields.tag) || getDictionaryText(itemFields.resourceType);
  const fileType = getFileExtension(file, getFieldValue(itemFields.fileType, 'PDF'));
  const fileSize = getFieldValue(itemFields.fileSize) || formatFileSize(file?.value?.size);
  const fileHref = file?.value?.src || itemFields.button?.value?.link?.value?.href || '#';

  return {
    id: String(item?.id ?? item?.name ?? title),
    title,
    description: stripHtml(
      getFieldValue(itemFields.description) || getFieldValue(itemFields.pageTitle)
    ),
    productLine: productLineValue,
    resourceType: resourceTypeValue,
    resourceTypeLabel: tagText,
    fileType,
    fileSize,
    isNew: Boolean(itemFields.isNew?.value),
    detailUrl: item?.fields.url?.value?.url || '#',
    coverImage: {
      src: productLineCovers[productLineValue] || '',
      alt: title,
    },
    downloadLink: {
      href: fileHref,
      target: '_blank',
    },
  };
};

const productLines = computed(() => (fields.value?.productLines || []).map(normalizeOption));
const resourceTypes = computed(() => [
  {
    id: 'all-resources',
    label: getRootFieldValue('allResourcesLabel', 'All Resources'),
    value: 'all-resources',
  },
  ...(fields.value?.resourceTypes || []).map(normalizeOption),
]);

const getOptionValue = (item: DocumentLibraryFilterOption) => item.value;
const getOptionLabel = (item: DocumentLibraryFilterOption) => item.label;

const getCurrentLanguage = () => {
  const page = (props as any).page;
  if (page?.itemLanguage) return page.itemLanguage;
  if (typeof document !== 'undefined') return document.documentElement.lang || 'en-US';
  return 'en-US';
};

const normalizeApiData = (res: any, ex: any) => res || ex || {};

const loadDocuments = async () => {
  const requestId = ++latestRequestId;
  isLoading.value = true;
  try {
    const [res, ex] = await api.documents.getDocuments({
      // lang: getCurrentLanguage(),
      keyword: searchKeyword.value.trim(),
      page: currentPage.value,
      size: pageSize.value,
      productLine: selectedProductLines.value.join(','),
      resourceType: selectedResourceTypes.value.includes('all-resources')
        ? ''
        : selectedResourceTypes.value.join(','),
    });
    if (requestId !== latestRequestId) return;

    const data = normalizeApiData(res, ex);
    documents.value = (data?.list || []).map(normalizeDocument);
    totalCount.value = Number(data?.total) || documents.value.length;
  } catch (error) {
    if (requestId !== latestRequestId) return;
    documents.value = [];
    totalCount.value = 0;
  } finally {
    if (requestId === latestRequestId) {
      isLoading.value = false;
    }
  }
};

const clearFilterReloadTimer = () => {
  if (!filterReloadTimer) return;
  clearTimeout(filterReloadTimer);
  filterReloadTimer = null;
};

const reloadFromFirstPage = () => {
  clearFilterReloadTimer();
  if (currentPage.value === 1) {
    loadDocuments();
    return;
  }
  currentPage.value = 1;
};

const debounceReloadFromFirstPage = () => {
  clearFilterReloadTimer();
  latestRequestId += 1;
  filterReloadTimer = setTimeout(() => {
    filterReloadTimer = null;
    reloadFromFirstPage();
  }, 1500);
};

const toggleSelected = (target: typeof selectedProductLines, value: string, checked: boolean) => {
  const next = new Set(target.value);
  if (checked) {
    next.add(value);
  } else {
    next.delete(value);
  }
  target.value = Array.from(next);
};

const onProductLineChange = (item: DocumentLibraryFilterOption, checked: boolean) => {
  toggleSelected(selectedProductLines, getOptionValue(item), checked);
  debounceReloadFromFirstPage();
};

const onResourceTypeChange = (item: DocumentLibraryFilterOption, checked: boolean) => {
  const value = getOptionValue(item);
  if (value === 'all-resources') {
    selectedResourceTypes.value = ['all-resources'];
    debounceReloadFromFirstPage();
    return;
  }
  if (checked) {
    selectedResourceTypes.value = selectedResourceTypes.value.filter(
      (selectedValue) => selectedValue !== 'all-resources'
    );
  }
  toggleSelected(selectedResourceTypes, value, checked);
  if (selectedResourceTypes.value.length === 0) {
    selectedResourceTypes.value = ['all-resources'];
  }
  debounceReloadFromFirstPage();
};

const onSearchChange = (_event: Event, value: string) => {
  searchKeyword.value = value;
  debounceReloadFromFirstPage();
};

const getDetailHref = (document: DocumentLibraryDocument) => document.detailUrl || '#';
const getCoverSrc = (document: DocumentLibraryDocument) => document.coverImage?.src || '';
const getCoverAlt = (document: DocumentLibraryDocument) =>
  document.coverImage?.alt || document.title;
const getCoverResourceType = (document: DocumentLibraryDocument) =>
  String(document.resourceTypeLabel || document.resourceType || '')
    .trim()
    .toUpperCase();
const getFileInfo = (document: DocumentLibraryDocument) =>
  [document.fileType || 'PDF', document.fileSize].filter(Boolean).join(' - ');

const toggleMobileFilters = () => {
  mobileFiltersOpen.value = !mobileFiltersOpen.value;
};

watch(currentPage, loadDocuments);
watch(pageSize, reloadFromFirstPage);
watch(
  () => route.query.kw,
  (value) => {
    const keyword = normalizeSearchKeyword(value);
    if (keyword === searchKeyword.value) return;
    searchKeyword.value = keyword;
    reloadFromFirstPage();
  }
);

onMounted(loadDocuments);
onBeforeUnmount(clearFilterReloadTimer);
</script>

<template>
  <section v-if="fields" :class="rootClass" :style="rootStyle">
    <ContentGrid
      :desktop-default-grid="[2, 20]"
      :tablet-default-grid="[1, 14]"
      :mobile-default-grid="[1, 14]"
    >
      <div class="c-document-library__inner">
        <aside
          class="c-document-library__filters"
          :class="{ 'is-open': mobileFiltersOpen }"
          aria-label="Document filters"
        >
          <div class="c-document-library__filters-header">
            <h2 v-if="getRootFieldValue('filtersTitle', 'Filters')">
              {{ getRootFieldValue('filtersTitle', 'Filters') }}
            </h2>
            <button
              class="c-document-library__mobile-filter-toggle"
              type="button"
              :aria-expanded="mobileFiltersOpen"
              @click="toggleMobileFilters"
            >
              <span>{{ getRootFieldValue('productLineTitle', 'Product Line') }}</span>
              <span class="c-document-library__mobile-filter-icon" aria-hidden="true"></span>
            </button>
          </div>

          <h2
            v-if="getRootFieldValue('filtersTitle', 'Filters')"
            class="c-document-library__filters-title c-document-library__filters-title--desktop"
          >
            {{ getRootFieldValue('filtersTitle', 'Filters') }}
          </h2>

          <div class="c-document-library__filter-group">
            <h3
              v-if="getRootFieldValue('productLineTitle', 'Product Line')"
              class="c-document-library__filter-heading"
            >
              {{ getRootFieldValue('productLineTitle', 'Product Line') }}
            </h3>
            <label
              v-for="item in productLines"
              :key="item.id || getOptionValue(item)"
              class="c-document-library__filter-option"
            >
              <Checkbox
                :value="selectedProductLines.includes(getOptionValue(item))"
                @change="(_event: Event, checked: boolean) => onProductLineChange(item, checked)"
              />
              <span>{{ getOptionLabel(item) }}</span>
            </label>
          </div>

          <div class="c-document-library__filter-group">
            <h3 class="c-document-library__filter-heading">
              {{ getRootFieldValue('resourceTypeTitle', 'Resource Type') }}
            </h3>
            <label
              v-for="item in resourceTypes"
              :key="item.id || getOptionValue(item)"
              class="c-document-library__filter-option"
            >
              <Checkbox
                :value="selectedResourceTypes.includes(getOptionValue(item))"
                @change="(_event: Event, checked: boolean) => onResourceTypeChange(item, checked)"
              />
              <span>{{ getOptionLabel(item) }}</span>
            </label>
          </div>
        </aside>

        <div class="c-document-library__content" :class="{ 'is-loading': isLoading }">
          <div class="c-document-library__toolbar">
            <h2 class="c-document-library__title">Document Library</h2>
            <FormInput
              class="c-document-library__search"
              :value="searchKeyword"
              :placeholder="getRootFieldValue('searchPlaceholder', 'Search')"
              data-name="document-library-search"
              @change="onSearchChange"
            >
              <template #icon>
                <svg class="c-document-library__search-icon" viewBox="0 0 16 16" aria-hidden="true">
                  <path
                    d="M7.2 12.4A5.2 5.2 0 1 1 7.2 2a5.2 5.2 0 0 1 0 10.4Zm0-1.2a4 4 0 1 0 0-8 4 4 0 0 0 0 8Zm3.7.55.85-.85 2.75 2.75-.85.85-2.75-2.75Z"
                    fill="currentColor"
                  />
                </svg>
              </template>
            </FormInput>
          </div>

          <div v-if="documents.length" class="c-document-library__list">
            <article
              v-for="document in documents"
              :key="document.id || document.title"
              class="c-document-library__item"
            >
              <a class="c-document-library__cover" :href="getDetailHref(document)" target="_block">
                <img
                  v-if="getCoverSrc(document)"
                  :src="getCoverSrc(document)"
                  :alt="getCoverAlt(document)"
                />
                <span v-if="getCoverResourceType(document)" class="c-document-library__cover-type">
                  {{ getCoverResourceType(document) }}
                </span>
              </a>

              <div class="c-document-library__meta">
                <div class="c-document-library__headline">
                  <span v-if="document.isNew" class="c-document-library__badge">New</span>
                  <h3 class="c-document-library__item-title">
                    <a :href="getDetailHref(document)" target="_block">{{ document.title }}</a>
                  </h3>
                </div>
                <p class="c-document-library__description">
                  {{ document.description }}
                </p>
                <p class="c-document-library__file-info">
                  {{ getFileInfo(document) }}
                </p>
              </div>

              <a
                class="c-document-library__download"
                :href="getDetailHref(document)"
                target="_block"
              >
                Learn More
              </a>
            </article>
          </div>

          <div v-else-if="isLoading" class="c-document-library__empty">Loading...</div>
          <div v-else class="c-document-library__empty">
            {{ getRootFieldValue('emptyMessage', 'No documents match your filters.') }}
          </div>
          <div
            v-if="isLoading && documents.length"
            class="c-document-library__loading"
            role="status"
            aria-live="polite"
          >
            <span class="c-document-library__loading-spinner" aria-hidden="true"></span>
            <span>Loading...</span>
          </div>

          <Pagination
            v-if="totalPages > 1"
            v-model:currentPage="currentPage"
            :totalPages="totalPages"
          />
        </div>
      </div>
    </ContentGrid>
  </section>
</template>
<style lang="scss">
@import '../../styles/variables';
@import '../../styles/functions';
@import '../../styles/mixins';

.c-document-library {
  padding: 48px 0 64px;
  background: $white;

  &__inner {
    display: grid;
    grid-template-columns: minmax(180px, 260px) minmax(0, 1fr);
    gap: 36px;
  }

  &__filters-header {
    display: none;
  }

  &__filters-title,
  &__title {
    margin: 0;
    color: #111827;
    font-size: 18px;
    font-weight: 700;
    line-height: 1.3;
  }

  &__title {
    opacity: 0;
    pointer-events: none;
  }

  &__filter-group {
    margin-top: 28px;
  }

  &__filter-heading {
    margin: 0 0 12px;
    color: #111827;
    font-size: 13px;
    font-weight: 700;
    line-height: 1.4;
  }

  &__filter-option {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-top: 8px;
    color: #606575;
    font-size: 12px;
    line-height: 1.4;
  }

  &__filter-option .e-form-checkbox__check {
    width: 13px;
    height: 13px;
    border-color: #7da98b;
    border-radius: 2px;
  }

  &__filter-option .e-form-checkbox.checked .e-form-checkbox__check {
    background: #7da98b;
    border-color: #7da98b;
  }

  &__toolbar {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 24px;
    margin-bottom: 22px;
  }

  &__search {
    width: min(100%, 240px);
  }

  &__search .e-form-input__main-input {
    height: 32px;
    border: 0;
    border-radius: 4px;
    background: #f4f4f4;
    padding: 0 36px 0 14px;
  }

  &__search .e-form-input__messages,
  &__search .e-form-input__title {
    display: none;
  }

  &__search .e-form-input__icon {
    right: 12px;
    color: #7da98b;
  }

  &__search-icon {
    width: 18px;
    height: 18px;
    display: block;
  }

  &__content {
    position: relative;
  }

  &__content.is-loading &__list {
    opacity: 0.45;
    pointer-events: none;
  }

  &__loading {
    position: absolute;
    inset: 72px 0 0;
    z-index: 2;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 10px;
    min-height: 180px;
    border-radius: 8px;
    background: rgba(255, 255, 255, 0.72);
    color: #606575;
    font-size: 14px;
    font-weight: 600;
  }

  &__loading-spinner {
    width: 20px;
    height: 20px;
    border: 2px solid #d7e1da;
    border-top-color: #7da98b;
    border-radius: 50%;
    animation: document-library-spin 0.8s linear infinite;
  }
  &__list {
    display: flex;
    flex-direction: column;
    gap: 18px;
  }

  &__item {
    position: relative;
    display: grid;
    grid-template-columns: 96px minmax(0, 1fr);
    gap: 22px;
    align-items: start;
    min-height: 126px;
  }

  &__meta {
    display: flex;
    min-height: 126px;
    flex-direction: column;
    justify-content: flex-start;
  }
  &__cover {
    width: 96px;
    height: 126px;
    border-radius: 4px;
    overflow: hidden;
    background: #f2f5f1;
  }

  &__cover img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  &__cover {
    position: relative;
    display: block;
    color: #111827;
    text-decoration: none;
  }

  &__cover-type {
    position: absolute;
    top: 13px;
    left: 10px;
    right: 8px;
    z-index: 1;
    color: #111827;
    font-size: 13px;
    font-weight: 400;
    line-height: 1.05;
    overflow-wrap: anywhere;
    text-transform: uppercase;
  }

  &__item-title a {
    color: inherit;
    text-decoration: none;
  }

  &__headline {
    display: flex;
    align-items: baseline;
    gap: 8px;
  }

  &__badge {
    display: inline-flex;
    align-items: center;
    height: 16px;
    padding: 0 6px;
    border-radius: 2px;
    background: #ff8a00;
    color: $white;
    font-size: 10px;
    font-weight: 700;
  }

  &__item-title {
    margin: 0;
    color: #111827;
    font-size: 15px;
    font-weight: 700;
    line-height: 1.35;
  }

  &__description {
    margin: 8px 0 0;
    color: #111827;
    font-size: 12px;
    line-height: 1.45;
  }

  &__file-info {
    margin: auto 0 0;
    color: #8b92a1;
    font-size: 12px;
    line-height: 1.4;
    text-transform: uppercase;
  }

  &__download {
    position: absolute;
    right: 0;
    bottom: 0;
    display: inline-flex;
    align-items: center;
    gap: 10px;
    color: #7da98b;
    font-size: 14px;
    font-weight: 500;
    line-height: 1;
    text-decoration: none;
  }

  &__download-icon {
    position: relative;
    display: inline-block;
    flex: 0 0 auto;
    width: 16px;
    height: 16px;
    color: currentColor;

    &::before {
      content: '';
      position: absolute;
      left: 7px;
      top: 1px;
      width: 1px;
      height: 11px;
      border-radius: 2px;
      background: currentColor;
    }

    &::after {
      content: '';
      position: absolute;
      left: 3px;
      top: 2px;
      width: 8px;
      height: 8px;
      border-right: 1.5px solid currentColor;
      border-bottom: 1.5px solid currentColor;
      transform: rotate(45deg);
    }
  }

  &__download-icon + &__download-icon {
    display: none;
  }

  &__download-icon {
    border-bottom: 1.5px solid currentColor;
  }

  &__download:visited,
  &__download:hover,
  &__download:active {
    color: #7da98b;
  }

  &__empty {
    border-radius: 8px;
    background: #f7f8f7;
    padding: 32px;
    color: #606575;
    text-align: center;
  }
}

@media (max-width: 1024px) {
  .c-document-library {
    &__inner {
      grid-template-columns: 1fr;
      gap: 28px;
    }

    &__filters {
      display: grid;
      grid-template-columns: repeat(2, minmax(0, 1fr));
      gap: 20px;
    }

    &__filters-title {
      grid-column: 1 / -1;
    }

    &__filter-group {
      margin-top: 0;
    }
  }
}

@media (max-width: 768px) {
  .c-document-library {
    padding: 32px 0 48px;

    &__inner {
      gap: 18px;
    }

    &__filters {
      display: block;
    }

    &__filters-header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 16px;
      margin-bottom: 16px;
    }

    &__filters-title {
      color: #3f3f46;
      font-size: 24px;
      line-height: 1.2;
    }

    &__filters-title--desktop {
      display: none;
    }

    &__mobile-filter-toggle {
      display: inline-flex;
      align-items: center;
      gap: 40px;
      border: 0;
      background: transparent;
      padding: 0;
      color: #000;
      font-size: 14px;
      font-weight: 700;
      line-height: 1.2;
      cursor: pointer;
    }

    &__mobile-filter-icon {
      width: 8px;
      height: 8px;
      border-top: 1px solid currentColor;
      border-left: 1px solid currentColor;
      transform: translateY(2px) rotate(45deg);
      transition: transform 0.2s ease;
    }

    &__filters:not(.is-open) &__filter-group {
      display: none;
    }

    &__filters.is-open &__mobile-filter-icon {
      transform: translateY(-2px) rotate(225deg);
    }

    &__filter-group {
      margin-top: 18px;
    }

    &__toolbar {
      display: block;
      margin-bottom: 22px;
    }

    &__title {
      display: none;
    }

    &__search {
      width: 100%;
      margin-top: 0;
    }

    &__search .e-form-input__main-input {
      height: 34px;
      border-radius: 6px;
      padding-left: 12px;
    }

    &__item {
      position: relative;
      display: grid;
      grid-template-columns: 130px minmax(0, 1fr);
      gap: 20px;
      align-items: start;
      min-height: auto;
    }

    &__meta {
      min-width: 0;
      min-height: 174px;
    }

    &__cover {
      width: 130px;
      height: 174px;
      border-radius: 4px;
    }

    &__cover-type {
      top: 15px;
      left: 11px;
      right: 9px;
      font-size: 20px;
    }

    &__headline {
      display: flex;
      flex-direction: row;
      align-items: flex-start;
      gap: 12px;
    }

    &__badge {
      flex: 0 0 auto;
      height: 24px;
      margin-top: 2px;
      padding: 0 6px;
      border-radius: 2px;
      background: #fff1e8;
      color: #ff7a1a;
      font-size: 12px;
      font-weight: 500;
      line-height: 1;
    }

    &__item-title {
      flex: 1;
      min-width: 0;
      font-size: 18px;
      line-height: 1.45;
    }

    &__description {
      display: block !important;
      margin-top: 10px;
      overflow: hidden;
      font-size: 16px;
      line-height: 1.4;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    &__file-info {
      margin-top: 28px;
      padding-right: 104px;
      font-size: 14px;
      line-height: 1.4;
      text-transform: uppercase;
    }

    &__download {
      position: absolute;
      right: 0;
      bottom: 2px;
      gap: 8px;
      font-size: 14px;
    }
  }
}

@keyframes document-library-spin {
  to {
    transform: rotate(360deg);
  }
}
</style>
